/**
 * Vista por defecto de /evaluations: marcos de evaluación (assessments)
 * agrupados por nivel. Es lo que ve el usuario común — no KPIs sueltos.
 *
 * Sub-nav (componente compartido `EvaluationsTabs`):
 *   - Evaluaciones (default)
 *   - Cuestionarios (admin/lead)
 *   - KPIs (admin/lead)
 *   - Histórico
 *   - Dashboard
 *
 * Cada AssessmentCard muestra el último resultado del período con score,
 * status, sparkline de 6 meses y breakdown colapsable (qué input aportó qué).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ChevronDown, Sparkles } from "lucide-react";
import {
  EVAL_LEVELS,
  EVAL_LEVEL_LABELS,
  EVAL_STATUS_LABELS,
  type Assessment,
  type AssessmentResult,
  type EvalStatus,
  type SessionUser,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { canSeeDef } from "@/lib/eval-visibility";
import { EvaluationsTabs } from "@/features/evaluations/EvaluationsTabs";
import { MetricSparkline } from "@/features/evaluations/MetricSparkline";

export const Route = createFileRoute("/evaluations/assessments")({
  component: AssessmentsPage,
});

interface ResultRow {
  result: AssessmentResult;
  assessment: Assessment;
}

function currentPeriod(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function AssessmentsPage() {
  const [period, setPeriod] = useState(currentPeriod());
  const qc = useQueryClient();

  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });

  const assessmentsQ = useQuery<{ assessments: Assessment[] }>({
    queryKey: ["assessments"],
    queryFn: () => api.get("/assessments"),
  });

  // Periodos para sparkline (6 meses).
  const sparklinePeriods = useMemo(() => buildLastNMonths(6), []);
  const from = sparklinePeriods[0]!;
  const to = period;

  const resultsQ = useQuery<{ results: ResultRow[] }>({
    queryKey: ["assessment-results", from, to],
    queryFn: () => api.get(`/assessments/results?periodFrom=${from}&periodTo=${to}`),
  });

  const runAll = useMutation({
    mutationFn: () => api.post(`/assessments/run-all?period=${period}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assessment-results"] }),
  });

  const visible = useMemo(() => {
    if (!me.data || !assessmentsQ.data) return [];
    return assessmentsQ.data.assessments.filter((a) => canSeeDef(a.visibility, me.data.role));
  }, [me.data, assessmentsQ.data]);

  // Index de resultados: key = `${assessmentId}|${projectId ?? ""}|${period}`
  const resultByKey = useMemo(() => {
    const m = new Map<string, AssessmentResult>();
    for (const row of resultsQ.data?.results ?? []) {
      m.set(keyFor(row.result.assessmentId, row.result.projectId, row.result.period), row.result);
    }
    return m;
  }, [resultsQ.data]);

  const canRun = me.data?.role === "admin" || me.data?.role === "lead";

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="evaluación continua"
        title="Evaluaciones"
        subtitle="Marcos que combinan KPIs y cuestionarios para devolver un estado por nivel. Inspirado en algoritmos evolutivos: medimos para evolucionar."
        actions={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm">
              <span className="text-xs uppercase tracking-wider text-base-content/55 hidden sm:inline">
                Período
              </span>
              <input
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value || currentPeriod())}
                className="input input-bordered input-sm"
              />
            </label>
            {canRun && (
              <button
                type="button"
                onClick={() => runAll.mutate()}
                disabled={runAll.isPending}
                className="btn btn-secondary btn-sm gap-1.5"
                title="Recalcular todos los assessments para el período"
              >
                {runAll.isPending ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  <Sparkles className="size-4" />
                )}
                Calcular
              </button>
            )}
          </div>
        }
      />

      <EvaluationsTabs current="assessments" role={me.data?.role ?? "member"} />

      {(assessmentsQ.isLoading || resultsQ.isLoading) && (
        <div className="grid place-items-center py-12">
          <span className="loading loading-spinner loading-md text-primary" />
        </div>
      )}

      {!assessmentsQ.isLoading &&
        EVAL_LEVELS.map((level) => {
          const list = visible.filter((a) => a.level === level && a.active);
          if (list.length === 0) return null;
          return (
            <section key={level} className="space-y-3">
              <h2 className="font-display text-xl font-semibold">{EVAL_LEVEL_LABELS[level]}</h2>
              <ul className="grid gap-3 md:grid-cols-2">
                {list.map((a) => (
                  <AssessmentCard
                    key={a.id}
                    assessment={a}
                    currentResult={resultByKey.get(keyFor(a.id, null, period))}
                    sparklinePoints={sparklinePeriods.map((p) => ({
                      period: p,
                      value: resultByKey.get(keyFor(a.id, null, p))?.score ?? null,
                    }))}
                  />
                ))}
              </ul>
            </section>
          );
        })}

      {!assessmentsQ.isLoading && visible.length === 0 && (
        <div className="card-elevated py-16 text-center">
          <p className="text-sm text-base-content/60">
            No tienes evaluaciones visibles. Pídele al admin que active alguna.
          </p>
        </div>
      )}
    </div>
  );
}

function AssessmentCard({
  assessment,
  currentResult,
  sparklinePoints,
}: {
  assessment: Assessment;
  currentResult: AssessmentResult | undefined;
  sparklinePoints: { period: string; value: number | null }[];
}) {
  const [open, setOpen] = useState(false);
  const status = (currentResult?.status as EvalStatus | undefined) ?? "unknown";

  return (
    <li>
      <article className={`card-elevated p-5 ${statusBorderClass(status)}`}>
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-semibold text-base-content leading-snug">
              {assessment.name}
            </h3>
            {assessment.description && (
              <p className="text-xs text-base-content/55 mt-1 line-clamp-2">
                {assessment.description}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end shrink-0">
            <span className={`text-3xl font-display font-semibold ${statusTextClass(status)}`}>
              {currentResult?.score != null ? Math.round(currentResult.score) : "—"}
            </span>
            <span className={`text-xs font-medium ${statusTextClass(status)}`}>
              {EVAL_STATUS_LABELS[status]}
            </span>
          </div>
        </header>

        <div className="mt-3 flex items-end gap-3">
          <MetricSparkline points={sparklinePoints} status={status} width={120} height={32} />
          <span className="text-xs text-base-content/45 mb-0.5">últimos 6 meses</span>
        </div>

        {currentResult?.breakdownJson && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="mt-3 w-full flex items-center justify-between text-xs text-base-content/65 hover:text-base-content py-1"
          >
            <span>Desglose · {parseBreakdown(currentResult.breakdownJson).length} inputs</span>
            <ChevronDown className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
        {open && currentResult?.breakdownJson && (
          <BreakdownList rows={parseBreakdown(currentResult.breakdownJson)} />
        )}
      </article>
    </li>
  );
}

interface BreakdownRow {
  inputKind: "kpi" | "questionnaire";
  inputId: string;
  inputName: string;
  weight: number;
  value: number | null;
  status: EvalStatus;
  score: number;
  contribution: number;
  required: boolean;
}

function BreakdownList({ rows }: { rows: BreakdownRow[] }) {
  return (
    <ul className="mt-2 space-y-1.5 text-xs">
      {rows.map((r) => (
        <li key={`${r.inputKind}:${r.inputId}`} className="flex items-center gap-2">
          <span
            className={`size-2 rounded-full ${statusDotClass(r.status)} shrink-0`}
            aria-hidden
          />
          <span className="flex-1 truncate text-base-content/80">{r.inputName}</span>
          <span className="text-base-content/45 font-mono tabular-nums">
            ×{r.weight.toFixed(2)}
          </span>
          <span className={`font-mono tabular-nums ${statusTextClass(r.status)}`}>
            {Math.round(r.score)}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────

function keyFor(assessmentId: string, projectId: string | null, period: string): string {
  return `${assessmentId}|${projectId ?? ""}|${period}`;
}

function buildLastNMonths(n: number): string[] {
  const months: string[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getUTCFullYear(), now.getUTCMonth() - i, 1);
    months.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return months;
}

function parseBreakdown(json: string): BreakdownRow[] {
  try {
    return JSON.parse(json);
  } catch {
    return [];
  }
}

function statusBorderClass(s: EvalStatus): string {
  switch (s) {
    case "excellent":
      return "border-l-4 border-l-info";
    case "good":
      return "border-l-4 border-l-success";
    case "improvable":
      return "border-l-4 border-l-warning";
    case "bad":
      return "border-l-4 border-l-error";
    default:
      return "";
  }
}

function statusTextClass(s: EvalStatus): string {
  switch (s) {
    case "excellent":
      return "text-info";
    case "good":
      return "text-success";
    case "improvable":
      return "text-warning";
    case "bad":
      return "text-error";
    default:
      return "text-base-content/55";
  }
}

function statusDotClass(s: EvalStatus): string {
  switch (s) {
    case "excellent":
      return "bg-info";
    case "good":
      return "bg-success";
    case "improvable":
      return "bg-warning";
    case "bad":
      return "bg-error";
    default:
      return "bg-base-300";
  }
}
